A cold sandbox launch otherwise reruns the slow build work on every provision:
the Docker image build (the browser/toolchain install layer), `bun install`, and
the production web build. The platform removes that cost by capturing known-good
outputs at VERIFICATION time and restoring them on every later provision. Most of
it is automatic; two things are your job: opt one-shot build services in with
labels, and keep their outputs instance-independent.

WHAT THE PLATFORM DOES FOR YOU (no action needed)
  - Prebuilt image: when a template verifies, the built image is pushed to a
    registry and PULLED on later provisions instead of rebuilt. Your services keep
    both `build:` and an injected `image:` — `--build` is skipped on a hit and a
    pull miss falls back to building. Nothing to declare.

WHAT YOU DECLARE — cache labels on one-shot build/dep services
  Same convention as the sandbox.route/sandbox.port labels. The platform snapshots
  the declared volumes/paths at verify and restores them at provision. How a HIT is
  reused depends on the service's REUSE MODE (auto-detected, override with
  sandbox.cache-reuse):
    - prime (dependency installs — a service caching a named VOLUME): the restore
      WARMS the install, but the command STILL RUNS. This is required: a package
      manager in a workspace monorepo writes a node_modules into EVERY package
      (apps/web/node_modules, packages/*/node_modules) that lives in the workspace,
      not in the one cached volume — so skipping the install on a hit leaves the tree
      incomplete and the app crashes. Re-running install warm rebuilds the COMPLETE
      tree. It is fast: the platform auto-provisions and caches the package manager's
      global store for you (see below).
    - skip (build outputs — a service caching only PATHS): the cache fully captures
      the output, so on a hit a marker file makes the one-shot a no-op (skips the
      rebuild). A miss runs cold and the next verify re-captures.

    services:
      deps:                         # dependency install → cache the volume (prime)
        labels:
          sandbox.cache: "true"
          sandbox.cache-key: "lockfiles"          # invalidate when a lockfile changes
          sandbox.cache-volumes: "node_modules"   # named volume(s) to snapshot
      web-build:                    # production build → cache the output dirs (skip)
        labels:
          sandbox.cache: "true"
          sandbox.cache-key: "inputs"
          sandbox.cache-inputs: "apps/web,packages/ui,bun.lock"   # source the build reads
          sandbox.cache-paths: "apps/web/dist,packages/ui/dist"   # output dirs to snapshot

  Labels:
    sandbox.cache: "true"        Opt this service in.
    sandbox.cache-key            How the cache is invalidated:
      "lockfiles"  — hash of the repo's lock manifests (bun.lock, package-lock.json,
                     yarn.lock, pnpm-lock.yaml). Use for dependency installs.
      "inputs"     — content hash of the sandbox.cache-inputs paths (node_modules
                     and dist excluded). Use for BUILDS. This is what makes the
                     cache useful on a branch that moves: a commit that does not
                     touch the build's inputs keeps the key → hit; one that does →
                     miss → rebuild. (Default: inputs when cache-inputs is set,
                     else lockfiles.)
    sandbox.cache-inputs         Comma-separated source paths the build consumes
                                 (required for "inputs"). List the app dir plus every
                                 workspace package it builds, plus the lockfile.
    sandbox.cache-volumes        Comma-separated named volumes to snapshot/restore.
    sandbox.cache-paths          Comma-separated workspace-relative output dirs to
                                 snapshot/restore.
    sandbox.cache-reuse          "prime" | "skip". Optional — defaults to prime for a
                                 service that caches a volume (install) and skip for a
                                 paths-only service (build output). Only override if
                                 the auto-detection is wrong for your service.
  All path/volume values must be plain, relative, traversal-free names — absolute
  paths, ".."/empty segments, or shell metacharacters are rejected (fail-closed).

  NEVER key a build on the git commit: a moving branch would invalidate it on every
  unrelated push (an api-only or docs commit) and the cache would never hit. Key on
  the build's actual INPUTS.

PACKAGE-MANAGER STORE IS AUTOMATIC (do not configure it)
  For a prime install service the platform sets the cache/store env vars for every
  common package manager (bun, npm, yarn, pnpm) to a shared, cached directory
  co-located with node_modules, and snapshots it. So the warm re-run of your install
  is offline and fast for ANY package manager with nothing to declare. Do NOT set
  BUN_INSTALL_CACHE_DIR / npm_config_cache / YARN_CACHE_FOLDER / store-dir yourself —
  the platform owns them. Just label the install service sandbox.cache: "true".

KEEP A SKIP SERVICE'S COMMAND ON ONE LINE
  For a skip (build-output) service the platform wraps its `command:` in a marker
  gate so a hit skips the build. It rewrites a single-line command — scalar
  (`command: bun run build`) or flow array (`command: ["bash","-c","..."]`). A
  multi-line block command is left ungated (still gets capture/restore but always
  runs). Keep it one line. (Prime install commands always run, so this does not
  apply to them.)

INSTANCE-INDEPENDENCE (required for cached build outputs)
  A cached `dist` is reused across DIFFERENT sandboxes, so it must not bake any
  per-sandbox value. The common trap is baking the API URL at build time
  (VITE_API_URL=${SANDBOX_URL}/api). Instead resolve the API origin at RUNTIME from
  the browser location — web and api are the same origin behind the sandbox nginx
  proxy, so a relative `/api` works on every sandbox. If you must bake a value, bake
  a fixed placeholder and rewrite it at provision; never bake ${SANDBOX_URL} into a
  cached output. Outputs that embed a per-sandbox value cannot be shared and will
  serve the wrong sandbox's URL.

DIAGNOSING A MISS
  Every provision logs one line per cacheable service:
    [CACHE] restore svc=web-build outcome=miss reason=miss-path key=a1b2c3d4 ms=140
  outcome is hit / prime / miss; reason names what stopped it (no-key, miss-path,
  miss-volume, extract-failed, no-mountpoint); key is the cache key that provision
  computed. The
  capture side logs the key it WROTE under ([CACHE] capture svc=... key=...), so two
  different keys means the inputs moved between capture and restore — usually a
  cache-inputs path listing something that changes on every commit.

REFRESHING THE CACHE
  Re-verify the template to re-capture (`canary sandbox verify <template-id>`).
  Editing artifacts already forces a re-verify. A miss is always safe — it just
  builds cold that one time.
